import { Field, Fieldset } from '@piggybank/form';
import { connect } from 'formik';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import uniq from 'lodash/uniq';
import T from 'prop-types';
import React, { memo } from 'react';
import { DynamicFormFieldFeedback } from './DynamicFormFieldFeedback';
import { DynamicFormHeading } from './DynamicFormHeading';
import { DynamicFormHint } from './DynamicFormHint';
import { getInputMetadata } from './DynamicFormInput';
import { DynamicFormLabel } from './DynamicFormLabel';
import { DynamicFormMoreInfo } from './DynamicFormMoreInfo';
import { Optional, TranslationScope } from './context';
import {
  formNameFromKeyPath,
  getDependencyFieldsKeyPath,
  localizationKeyPath,
  requiredArg,
  useFormikSync,
} from './utils';

const getFieldName = keyPath => formNameFromKeyPath(keyPath).join('.');

export const DynamicFormFieldComponent = ({
  context = requiredArg('context'),
  formValues = requiredArg('formValues'),
  validate = requiredArg('validate'),
  schemaNode = requiredArg('schemaNode'),
  renderChildren = requiredArg('renderChildren'),
  formik,
  readOnly,
  getPageLevelErrors = requiredArg('getPageLevelErrors'),
}) => {
  const { isCheckboxGroup, keyPath } = context;
  const fieldName = getFieldName(keyPath);
  const sanitizedKeyPath = localizationKeyPath(keyPath);

  useFormikSync({
    context,
    fieldName,
    formValues,
    formik,
    validate,
  });

  const { componentRendersOwnLabel, isFieldset } = getInputMetadata({
    schemaNode,
    context,
  });

  const FieldElement = isFieldset ? Fieldset : Field;

  const dynamicFormFieldFeedbackProps = {
    fieldName,
    errors: getPageLevelErrors(),
  };

  return (
    <FieldElement
      key={sanitizedKeyPath.join('-')}
      marginBottom={isCheckboxGroup || readOnly ? 0 : 5}
      name={fieldName}
    >
      <TranslationScope path={sanitizedKeyPath}>
        <Optional>
          <DynamicFormHeading
            context={context}
            formValues={formValues}
            schemaNode={schemaNode}
          />
        </Optional>
        {!componentRendersOwnLabel && !readOnly && (
          <>
            <Optional>
              <DynamicFormLabel
                context={context}
                formValues={formValues}
                isFieldset={isFieldset}
                schemaNode={schemaNode}
              />
            </Optional>
            <Optional>
              <DynamicFormHint context={context} formValues={formValues} />
            </Optional>
          </>
        )}
        {renderChildren({ context, formValues })}
        <DynamicFormFieldFeedback {...dynamicFormFieldFeedbackProps} />
        <Optional>
          <DynamicFormMoreInfo
            formValues={formValues}
            path={sanitizedKeyPath}
          />
        </Optional>
      </TranslationScope>
    </FieldElement>
  );
};

DynamicFormFieldComponent.propTypes = {
  context: T.shape({
    keyPath: T.object,
    schemaPath: T.arrayOf(T.oneOfType([T.string, T.number])),
    isCheckboxGroup: T.bool,
  }),
  formValues: T.object,
  validate: T.func,
  schemaNode: T.object,
  renderChildren: T.func,
  readOnly: T.bool,
  formik: T.shape({
    setFieldValue: T.func,
    validate: T.func,
  }),
  getPageLevelErrors: T.func,
};

export function preventUpdate(prevProps, nextProps) {
  // If $customInput requires non-standard form-structure
  if (prevProps.doUpdateFormStructureForPath) {
    prevProps.doUpdateFormStructureForPath();
  }

  if (global.__DEV__) {
    return false;
  }

  // when one field is replaced by the other field, i.e. different schemaPath
  // component need to be updated
  if (!isEqual(prevProps.context.schemaPath, nextProps.context.schemaPath))
    return false;

  const fieldName = getFieldName(prevProps.context.keyPath);

  // If is part of an addAnother and item was removed.
  if (typeof prevProps.context.keyPath.index !== 'undefined') {
    if (isEmpty(get(prevProps, `formValues.${fieldName}`, {}))) {
      return false;
    }
  }

  // if the field has formik error, update component
  if (get(nextProps, `formik.errors.${fieldName}`, null)) return false;

  // if excludeOptions has changed, update component
  if (
    !isEqual(prevProps.context.excludeOptions, nextProps.context.excludeOptions)
  )
    return false;

  const sanitizedKeyPath = localizationKeyPath(prevProps.context.keyPath);

  const fieldTranslation = get(
    prevProps.context.translations,
    sanitizedKeyPath,
    {}
  );

  const translationDependencies = Object.values(fieldTranslation).reduce(
    function recurse(acc, value) {
      if (isArray(value)) {
        return [
          ...acc,
          ...getDependencyFieldsKeyPath(value, sanitizedKeyPath).map(
            fieldKeyPath => fieldKeyPath.join('.')
          ),
        ];
      } else if (isObject(value)) {
        return Object.values(value).reduce(recurse, [...acc]);
      }

      return acc;
    },
    []
  );

  // when field itself or its dependencies  has value changed, update component
  return uniq([fieldName, ...translationDependencies]).every(fieldName =>
    isEqual(
      get(prevProps, `formValues.${fieldName}`, null),
      get(nextProps, `formValues.${fieldName}`, null)
    )
  );
}

const WrappedDynamicFormField = connect(
  memo(DynamicFormFieldComponent, preventUpdate)
);

export { WrappedDynamicFormField as DynamicFormField };
